/**
 * Bills & receipts — redesign v3 "Bills" frame. Two stat tiles (paid this
 * year, on-time streak) above the year-grouped list; desktop (≥1024) shows
 * the same table treatment as the "Payments desktop" bill table (invoice /
 * period / amount / status / receipt columns — the doc notes table column
 * specs are "consistent across boards — copy patterns verbatim").
 * Wired: GET /api/payments/history. Port of design/source/screens-account.jsx
 * BillsHistory, extended with year grouping per brief.
 */
import React from 'react';
import { Platform, Pressable, StyleSheet, View, useWindowDimensions } from 'react-native';

import { radius, space } from '@bero/tokens';
import { str } from '@/components/gate/api';
import { backOr, billPillLabel, billPillStatus, inr, invoiceCode, monthYear, shortDate } from '@/components/money/bits';
import { fetchBillHistory, fetchPayTo, useAsync, type Bill, type PayTo } from '@/components/money/api';
import { invoiceHtml, printInvoice } from '@/components/money/invoice';
import { Icon } from '@/components/money/icons';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useSession } from '@/lib/session';
import { useTheme } from '@/theme';
import { AppText, EmptyState, Hairline, Pill, Screen, Skeleton, TopBar } from '@/ui';

const WIDE = 1024;

function currentYear(): string {
  return String(new Date().getFullYear());
}

/**
 * Consecutive-months-paid streak, counted back from the most recent bill.
 * A leading bill that is DUE (generated but not yet due/overdue) doesn't
 * count against the streak — it hasn't been missed, it just hasn't
 * happened yet. Only OVERDUE or a PAID-after-its-due-date bill breaks it
 * (F4 P1-9).
 */
function onTimeStreak(bills: Bill[]): number {
  const sorted = [...bills].sort((a, b) => b.period.localeCompare(a.period));
  let i = 0;
  while (i < sorted.length && sorted[i].status === 'DUE') i += 1; // skip leading not-yet-due bill(s)
  let n = 0;
  for (; i < sorted.length; i++) {
    const b = sorted[i];
    if (b.status !== 'PAID') break;
    const paidAt = b.payments[0]?.paidAt;
    if (paidAt && b.dueDate && new Date(paidAt).getTime() > new Date(b.dueDate).getTime()) break; // paid late
    n += 1;
  }
  return n;
}

export default function BillsHistoryScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { width } = useWindowDimensions();
  const wide = Platform.OS === 'web' && width >= WIDE;
  const { user } = useSession();
  const { data: bills, loading, error, refetch } = useAsync(() => fetchBillHistory(100), []);

  // Invoice/receipt context — best-effort, the PDF just omits what's missing.
  const [societyName, setSocietyName] = React.useState<string | null>(null);
  const [payTo, setPayTo] = React.useState<PayTo | null>(null);
  const [printNotice, setPrintNotice] = React.useState<string | null>(null);
  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const body = await api.get<{ societies?: unknown[] }>('/api/societies');
        const mine = (body.societies ?? []).find((s) => (s as { id?: number })?.id === user?.societyId);
        if (!cancelled && mine) setSocietyName(str(mine, 'name') ?? null);
      } catch { /* header just says "Housing society" */ }
      try {
        const p = await fetchPayTo();
        if (!cancelled) setPayTo(p);
      } catch { /* invoice omits the how-to-pay block */ }
    })();
    return () => { cancelled = true; };
  }, [user?.societyId]);

  const openPdf = (b: Bill) => {
    const html = invoiceHtml(b, {
      societyName,
      residentName: user?.fullName,
      flatCode: b.flatCode ?? user?.flat?.code,
      payTo,
    });
    void printInvoice(html).then((ok) => {
      if (!ok) setPrintNotice(t('bh_print_notice'));
    });
  };

  const groups = React.useMemo(() => {
    const byYear = new Map<string, Bill[]>();
    for (const b of bills ?? []) {
      const year = b.period.slice(0, 4);
      const list = byYear.get(year) ?? [];
      list.push(b);
      byYear.set(year, list);
    }
    return Array.from(byYear.entries()).sort((a, b) => Number(b[0]) - Number(a[0]));
  }, [bills]);

  const paidThisYear = React.useMemo(() => {
    const year = currentYear();
    return (bills ?? [])
      .filter((b) => b.period.startsWith(year) && b.status === 'PAID')
      .reduce((sum, b) => sum + b.totalAmount, 0);
  }, [bills]);

  const streak = React.useMemo(() => onTimeStreak(bills ?? []), [bills]);

  return (
    <Screen scroll={false}>
      <TopBar title={t('bh_title')} onBack={backOr('/(tabs)/pay')} />
      <Screen padded style={{ paddingTop: space.s3 }}>
        {loading ? (
          <View style={{ gap: space.s3 }}>
            {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} h={56} />)}
          </View>
        ) : error ? (
          <EmptyState
            title={t('bh_err_title')}
            body={t('bh_err_body')}
            cta={t('retry')}
            onCta={() => void refetch()}
          />
        ) : !bills || bills.length === 0 ? (
          <EmptyState
            icon={<Icon name="calendar" size={40} color={colors.textTertiary} />}
            title={t('bh_empty_title')}
            body={t('bh_empty_body')}
          />
        ) : (
          <>
            <View style={{ flexDirection: 'row', gap: space.s3 }}>
              <StatTile label={t('bh_paid_in', { year: currentYear() })} value={inr(paidThisYear)} />
              <StatTile label={t('bh_streak')} value={t('bh_months', { n: streak })} tint={colors.primary500} />
            </View>
            {printNotice ? (
              <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s3 }}>{printNotice}</AppText>
            ) : null}

            {wide ? (
              <View style={{
                marginTop: space.s5, borderWidth: StyleSheet.hairlineWidth, borderColor: colors.borderSoft,
                borderRadius: radius.xl, overflow: 'hidden',
              }}>
                <View style={{
                  flexDirection: 'row', paddingVertical: space.s3, paddingHorizontal: space.s5,
                  borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.borderSoft,
                }}>
                  <AppText variant="label" tone="tertiary" style={{ flex: 1.4 }}>{t('pay_col_invoice')}</AppText>
                  <AppText variant="label" tone="tertiary" style={{ flex: 1 }}>{t('pay_col_period')}</AppText>
                  <AppText variant="label" tone="tertiary" style={{ flex: 1, textAlign: 'right' }}>{t('pay_col_amount')}</AppText>
                  <AppText variant="label" tone="tertiary" style={{ flex: 0.8, textAlign: 'center' }}>{t('pay_col_status')}</AppText>
                  <AppText variant="label" tone="tertiary" style={{ flex: 0.6, textAlign: 'right' }}>{t('bh_col_receipt')}</AppText>
                </View>
                {bills.map((b, i) => (
                  <View key={b.id} style={{
                    flexDirection: 'row', alignItems: 'center', paddingVertical: space.s3, paddingHorizontal: space.s5,
                    borderBottomWidth: i === bills.length - 1 ? 0 : StyleSheet.hairlineWidth, borderBottomColor: colors.borderSoft,
                  }}>
                    <AppText mono style={{ flex: 1.4, fontSize: 12, color: colors.textSecondary }}>{invoiceCode(b.period, b.id)}</AppText>
                    <AppText variant="bodySm" style={{ flex: 1 }}>{monthYear(b.period)}</AppText>
                    <AppText mono variant="bodySm" style={{ flex: 1, textAlign: 'right' }}>{inr(b.totalAmount)}</AppText>
                    <View style={{ flex: 0.8, alignItems: 'center' }}>
                      <Pill status={billPillStatus(b.status)} label={billPillLabel(b.status)} />
                    </View>
                    <Pressable accessibilityRole="button" accessibilityLabel={t('bh_download_a11y', { month: monthYear(b.period), doc: b.status === 'PAID' ? t('bh_receipt') : t('bh_invoice') })}
                      onPress={() => openPdf(b)} hitSlop={8} style={{ flex: 0.6, alignItems: 'flex-end' }}>
                      <AppText variant="bodySm" weight="600" tone="brand">PDF</AppText>
                    </Pressable>
                  </View>
                ))}
              </View>
            ) : (
              groups.map(([year, list]) => (
                <View key={year} style={{ marginTop: space.s4 }}>
                  <AppText variant="label" tone="tertiary" style={{ marginBottom: 4 }}>{year}</AppText>
                  {list.map((b, i) => (
                    <View key={b.id}>
                      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 14 }}>
                        <View>
                          <AppText variant="bodyMd" weight="500">{monthYear(b.period)}</AppText>
                          <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                            {b.status === 'PAID' && b.payments[0]
                              ? `${t('bh_paid_on', { date: shortDate(b.payments[0].paidAt) })}${b.lateFee > 0 ? ` · ${t('bh_incl_late')}` : ''}`
                              : b.status === 'OVERDUE'
                                ? t('pay_overdue_since', { date: shortDate(b.dueDate) })
                                : t('pay_due_by', { date: shortDate(b.dueDate) })}
                          </AppText>
                        </View>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                          <AppText variant="headingSm" weight="500" mono>{inr(b.totalAmount)}</AppText>
                          <Pill status={billPillStatus(b.status)} label={billPillLabel(b.status)} />
                          <Pressable accessibilityRole="button" accessibilityLabel={t('bh_download_a11y', { month: monthYear(b.period), doc: b.status === 'PAID' ? t('bh_receipt') : t('bh_invoice') })}
                            onPress={() => openPdf(b)} hitSlop={10}>
                            <AppText variant="bodySm" weight="600" tone="brand">PDF</AppText>
                          </Pressable>
                        </View>
                      </View>
                      {i < list.length - 1 ? <Hairline /> : null}
                    </View>
                  ))}
                </View>
              ))
            )}
          </>
        )}
      </Screen>
    </Screen>
  );
}

function StatTile({ label, value, tint }: { label: string; value: string; tint?: string }) {
  const { colors } = useTheme();
  return (
    <View style={{
      flex: 1, borderWidth: StyleSheet.hairlineWidth, borderColor: colors.borderSoft,
      borderRadius: radius.lg, padding: space.s4,
    }}>
      <AppText variant="label" tone="tertiary">{label}</AppText>
      <AppText mono weight="500" style={{ fontSize: 22, lineHeight: 28, marginTop: 4, color: tint ?? colors.textPrimary }}>
        {value}
      </AppText>
    </View>
  );
}
